// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\B2BCampaignDetails.jsx
// Название: B2B Campaign Details Page
// Версия: 1.0.0
// Дата: 29.12.2025

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';

const B2BCampaignDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // ============================================
  // STATE
  // ============================================
  const [campaign, setCampaign] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [stats, setStats] = useState({
    total: 0, distributed: 0, activated: 0, spent: 0,
    activationRate: 0, spentRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // ============================================
  // LOAD DATA
  // ============================================
  useEffect(() => {
    loadCampaignDetails();
  }, [id]);

  const loadCampaignDetails = async () => {
    setLoading(true);
    try {
      // Загружаем кампанию
      const campaignRes = await api.get('/campaigns/' + id);
      const campaignData = campaignRes.data || campaignRes;
      setCampaign(campaignData);
      
      // Загружаем результаты (получатели/подарки)
      const resultsRes = await api.get('/campaigns/' + id + '/results');
      const resultsData = resultsRes.data || resultsRes;
      
      const gifts = resultsData.gifts || resultsData.data?.gifts || [];
      setRecipients(gifts);
      
      // Считаем статистику
      const total = gifts.length;
      const distributed = gifts.filter(g => g.distributed_at).length;
      const activated = gifts.filter(g => g.claimed_at).length;
      const totalAmount = gifts.reduce((sum, g) => sum + parseFloat(g.amount || 0), 0);
      const activatedAmount = gifts.filter(g => g.claimed_at).reduce((sum, g) => sum + parseFloat(g.amount || 0), 0);
      
      setStats({
        total,
        distributed,
        activated,
        spent: 0, // TODO: из redemption tracking
        totalAmount,
        activatedAmount,
        activationRate: total > 0 ? Math.round((activated / total) * 100) : 0,
        spentRate: 0
      });
      
    } catch (err) {
      console.error('Load campaign error:', err);
      setError('Ошибка загрузки кампании');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // FILTERS
  // ============================================
  const filteredRecipients = recipients.filter(r => {
    // Фильтр по статусу
    if (filter === 'activated' && !r.claimed_at) return false;
    if (filter === 'distributed' && (!r.distributed_at || r.claimed_at)) return false;
    if (filter === 'pending' && r.distributed_at) return false;
    
    // Поиск
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const name = (r.name || '').toLowerCase();
      const email = (r.email || '').toLowerCase();
      if (!name.includes(search) && !email.includes(search)) return false;
    }
    
    return true;
  });

  // ============================================
  // HELPERS
  // ============================================
  const getStatusBadge = (recipient) => {
    if (recipient.claimed_at) return { text: '✅ Активирован', bg: '#dcfce7', color: '#166534' };
    if (recipient.distributed_at) return { text: '📧 Отправлен', bg: '#dbeafe', color: '#1e40af' };
    return { text: '⏳ Ожидает', bg: '#fef3c7', color: '#92400e' };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getOccasionLabel = (occasion) => {
    const map = {
      gift: '🎁 Подарок', birthday: '🎂 День рождения', newyear: '🎄 Новый год',
      christmas: '🎅 Рождество', bonus: '💰 Бонус', thankyou: '🙏 Благодарность',
      welcome: '👋 Welcome', achievement: '🏆 Достижение', corporate: '🏢 Корпоратив',
      employee: '👔 Сотрудникам', partner: '🤝 Партнёрам', loyalty: '❤️ Лояльность'
    };
    return map[occasion] || '🎁 Подарок';
  };

  // ============================================
  // STYLES
  // ============================================
  const styles = {
    container: { padding: 24, maxWidth: 1200, margin: '0 auto' },
    
    // Header
    header: { marginBottom: 24 },
    backBtn: { 
      background: 'none', border: 'none', color: '#6b7280', fontSize: 14, 
      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16,
      padding: 0
    },
    titleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 },
    title: { fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 },
    badge: { padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 500 },
    meta: { display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 },
    metaItem: { fontSize: 14, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 },
    
    // Stats Grid
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 },
    statCard: { 
      background: 'white', borderRadius: 12, padding: 20, 
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)', textAlign: 'center'
    },
    statValue: { fontSize: 28, fontWeight: 700, marginBottom: 4 },
    statLabel: { fontSize: 13, color: '#6b7280' },
    
    // Actions
    actionsBar: { 
      background: 'white', borderRadius: 12, padding: 16, marginBottom: 24,
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)', display: 'flex', gap: 12, flexWrap: 'wrap'
    },
    btnPrimary: { 
      padding: '10px 20px', background: '#10B981', color: 'white', 
      border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 6
    },
    btnSecondary: { 
      padding: '10px 20px', background: '#f3f4f6', color: '#374151', 
      border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 6
    },
    
    // Filters
    filtersBar: { 
      display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center'
    },
    searchInput: { 
      padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 8, 
      fontSize: 14, width: 250
    },
    filterBtn: { 
      padding: '8px 14px', background: '#f3f4f6', border: 'none', 
      borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#6b7280'
    },
    filterBtnActive: { background: '#10B981', color: 'white' },
    
    // Table
    tableCard: { 
      background: 'white', borderRadius: 12, overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
    },
    tableHeader: { 
      padding: '16px 20px', borderBottom: '1px solid #e5e7eb', 
      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
    },
    tableTitle: { fontSize: 16, fontWeight: 600, color: '#111827' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { 
      textAlign: 'left', padding: '12px 16px', borderBottom: '2px solid #e5e7eb', 
      fontSize: 12, color: '#6b7280', fontWeight: 600, background: '#f9fafb'
    },
    td: { padding: '14px 16px', borderBottom: '1px solid #f3f4f6', fontSize: 14 },
    
    // Recipient row
    recipientInfo: { display: 'flex', alignItems: 'center', gap: 12 },
    avatar: { 
      width: 40, height: 40, borderRadius: '50%', objectFit: 'cover',
      background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 14, fontWeight: 600, color: '#6b7280'
    },
    recipientName: { fontWeight: 500, color: '#111827' },
    recipientEmail: { fontSize: 13, color: '#6b7280' },
    
    // Empty & Loading
    empty: { textAlign: 'center', padding: 40, color: '#6b7280' },
    loading: { textAlign: 'center', padding: 60, color: '#6b7280' }
  };

  // ============================================
  // RENDER
  // ============================================
  if (loading) {
    return <div style={styles.loading}>⏳ Загрузка кампании...</div>;
  }

  if (error || !campaign) {
    return (
      <div style={styles.container}>
        <div style={styles.empty}>
          <p>❌ {error || 'Кампания не найдена'}</p>
          <button style={styles.btnSecondary} onClick={() => navigate('/business/b2b/campaigns')}>
            ← К списку кампаний
          </button>
        </div>
      </div>
    );
  }

  const statusColor = campaign.status === 'ready' ? '#10B981' : 
                      campaign.status === 'draft' ? '#f59e0b' : '#6b7280';

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate('/business/b2b/campaigns')}>
          ← Назад к кампаниям
        </button>
        
        <div style={styles.titleRow}>
          <h1 style={styles.title}>{campaign.name}</h1>
          <span style={{...styles.badge, background: statusColor + '20', color: statusColor}}>
            {campaign.status === 'ready' ? '✅ Активна' : 
             campaign.status === 'draft' ? '📝 Черновик' : campaign.status}
          </span>
        </div>
        
        <div style={styles.meta}>
          <span style={styles.metaItem}>{getOccasionLabel(campaign.occasion)}</span>
          <span style={styles.metaItem}>💰 {campaign.reward_amount} DAR/чел</span>
          <span style={styles.metaItem}>📅 {formatDate(campaign.created_at)}</span>
          {campaign.expires_at && (
            <span style={styles.metaItem}>⏰ До {formatDate(campaign.expires_at)}</span>
          )}
        </div>
      </div>

      {/* Stats */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={{...styles.statValue, color: '#374151'}}>{stats.total}</div>
          <div style={styles.statLabel}>Получателей</div>
        </div>
        <div style={styles.statCard}>
          <div style={{...styles.statValue, color: '#3b82f6'}}>{stats.distributed}</div>
          <div style={styles.statLabel}>Отправлено</div>
        </div>
        <div style={styles.statCard}>
          <div style={{...styles.statValue, color: '#10B981'}}>{stats.activated}</div>
          <div style={styles.statLabel}>Активировано</div>
        </div>
        <div style={styles.statCard}>
          <div style={{...styles.statValue, color: '#8b5cf6'}}>{stats.activationRate}%</div>
          <div style={styles.statLabel}>Конверсия</div>
        </div>
      </div>

      {/* Actions */}
      <div style={styles.actionsBar}>
        <button style={styles.btnPrimary} onClick={() => navigate('/business/campaigns/' + id + '/flux')}>
          🎨 AI Wizard
        </button>
        <button style={styles.btnSecondary} onClick={loadCampaignDetails}>
          🔄 Обновить
        </button>
        <button style={styles.btnSecondary}>
          📥 Скачать отчёт
        </button>
        <button style={styles.btnSecondary}>
          📧 Отправить всем
        </button>
      </div>

      {/* Filters */}
      <div style={styles.filtersBar}>
        <input
          type="text"
          placeholder="🔍 Поиск по имени или email..."
          style={styles.searchInput}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {['all', 'pending', 'distributed', 'activated'].map(f => (
          <button
            key={f}
            style={{...styles.filterBtn, ...(filter === f ? styles.filterBtnActive : {})}}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? `Все (${recipients.length})` : 
             f === 'pending' ? `Ожидают (${recipients.filter(r => !r.distributed_at).length})` :
             f === 'distributed' ? `Отправлено (${recipients.filter(r => r.distributed_at && !r.claimed_at).length})` :
             `Активировано (${recipients.filter(r => r.claimed_at).length})`}
          </button>
        ))}
      </div>

      {/* Recipients Table */}
      <div style={styles.tableCard}>
        <div style={styles.tableHeader}>
          <span style={styles.tableTitle}>👥 Получатели ({filteredRecipients.length})</span>
        </div>
        
        {filteredRecipients.length === 0 ? (
          <div style={styles.empty}>
            <p>Нет получателей</p>
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Получатель</th>
                <th style={styles.th}>Сумма</th>
                <th style={styles.th}>Статус</th>
                <th style={styles.th}>Отправлен</th>
                <th style={styles.th}>Активирован</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecipients.map(recipient => {
                const status = getStatusBadge(recipient);
                return (
                  <tr key={recipient.id || recipient.gift_id}>
                    <td style={styles.td}>
                      <div style={styles.recipientInfo}>
                        {recipient.image_url ? (
                          <img src={recipient.image_url} alt="" style={styles.avatar} />
                        ) : (
                          <div style={styles.avatar}>
                            {(recipient.name || '?')[0]}
                          </div>
                        )}
                        <div>
                          <div style={styles.recipientName}>{recipient.name || 'Получатель'}</div>
                          <div style={styles.recipientEmail}>{recipient.email || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={{ fontWeight: 600, color: '#10B981' }}>
                        {parseFloat(recipient.amount || 0).toFixed(0)} DAR
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        background: status.bg,
                        color: status.color,
                        padding: '4px 10px',
                        fontSize: 12
                      }}>
                        {status.text}
                      </span>
                    </td>
                    <td style={styles.td}>{formatDate(recipient.distributed_at)}</td>
                    <td style={styles.td}>{formatDate(recipient.claimed_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default B2BCampaignDetails;